import { http } from './http-client';

import type {
  AssignClientCategoryPayload,
  BulkUpdateResponse,
  ChildDto,
  ClientCategoryStandingDto,
  ClientFriendsResponse,
  ClientSortKey,
  ForgetClientBody,
  LinkChildAtDeskRequest,
  PaySplitDebtResponse,
  SetClientParallelCourtsPayload,
  SortOrder,
  StaffFriendDto,
  StaffPendingCounts,
  UpdateClientHighlightPayload,
  UpdateClientNotePayload,
  CheckinCardDto as GenCheckinCardDto,
  ChildCategoriesDto as GenChildCategoriesDto,
  OutstandingDefaultedSplitDto as GenOutstandingDefaultedSplitDto,
  AdminCorrectFioPayload as GenAdminCorrectFioPayload,
  BulkUpdatePayload as GenBulkUpdatePayload,
} from './generated';

export type {
  AssignClientCategoryPayload,
  BulkUpdateResponse,
  ChildDto,
  ClientCategoryStandingDto,
  ClientFriendsResponse,
  LinkChildAtDeskRequest,
  StaffFriendDto,
  StaffPendingCounts,
  UpdateClientHighlightPayload,
  UpdateClientNotePayload,
};

export type CheckinCard = GenCheckinCardDto;
export type ChildCategories = GenChildCategoriesDto;
export type OutstandingDefaultedSplit = GenOutstandingDefaultedSplitDto;

import type { SportCode } from './sports-api';
import type { ClientActivity } from './generated';
import type {
  ClientDetailDto as GenClientDetailDto,
  ClientListItemDto,
  ClientPaymentDto,
  ClientVisitDto,
  ClientPaymentStatus as GenClientPaymentStatus,
  ClientSearchResultDto,
  ClientVisitStatus as GenClientVisitStatus,
} from './generated';

export type ClientCategory = 'standard' | 'business' | 'elite' | 'vip' | (string & {});

export type ClientSearchResult = ClientSearchResultDto;

export type ClientListItem = ClientListItemDto;

export interface ClientListFilters {
  q?: string;
  activity?: ClientActivity[];
  category?: ClientCategory[];
  sport?: SportCode[];
  includeBlacklisted?: boolean;
}

export interface ClientListParams extends ClientListFilters {
  sort?: `${ClientSortKey}`;
  order?: `${SortOrder}`;
  page?: number;
  pageSize?: number;
  hasBonuses?: boolean;
}

export interface ClientListResponse {
  items: ClientListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export type ClientVisitStatus = `${GenClientVisitStatus}`;

export type ClientVisitItem = ClientVisitDto;


export type ClientPaymentStatus = `${GenClientPaymentStatus}`;

export type ClientPaymentItem = ClientPaymentDto;


export type ClientDetailDto = GenClientDetailDto;

export type ClientBulkAction = 'set-blacklist' | 'unset-blacklist';

export type BulkUpdateRequest = Omit<GenBulkUpdatePayload, 'action'> & { action: ClientBulkAction };

export type AdminCorrectProfilePayload = Omit<GenAdminCorrectFioPayload, 'reason'> & { reason: string };

export const clientsApi = {
  search(q: string, limit = 8, call?: { signal?: AbortSignal }): Promise<{ items: ClientSearchResult[] }> {
    return http.get<{ items: ClientSearchResult[] }>('/clients/search', {
      query: { q, limit },
      signal: call?.signal,
    });
  },

  recent(call?: { signal?: AbortSignal }): Promise<{ items: ClientSearchResult[] }> {
    return http.get<{ items: ClientSearchResult[] }>('/clients/recent', { signal: call?.signal });
  },

  checkinCards(ids: readonly string[], call?: { signal?: AbortSignal }): Promise<{ items: CheckinCard[] }> {
    return http.get<{ items: CheckinCard[] }>('/clients/checkin-cards', {
      query: { ids: ids.join(',') },
      signal: call?.signal,
    });
  },

  list(params: ClientListParams, call?: { signal?: AbortSignal }): Promise<ClientListResponse> {
    return http.get<ClientListResponse>('/clients', {
      query: {
        q: params.q,
        activity: params.activity?.join(','),
        category: params.category?.join(','),
        sport: params.sport?.join(','),
        includeBlacklisted: params.includeBlacklisted,
        sort: params.sort,
        order: params.order,
        page: params.page,
        pageSize: params.pageSize,
        hasBonuses: params.hasBonuses,
      },
      signal: call?.signal,
    });
  },

  get(id: string, call?: { signal?: AbortSignal }): Promise<ClientDetailDto> {
    return http.get<ClientDetailDto>(`/clients/${id}`, { signal: call?.signal });
  },

  updateNote(id: string, payload: UpdateClientNotePayload): Promise<void> {
    return http.patch<void>(`/clients/${id}/note`, payload);
  },

  updateHighlight(
    id: string,
    payload: UpdateClientHighlightPayload,
  ): Promise<void> {
    return http.patch<void>(`/clients/${id}/highlight`, payload);
  },

  removePhoto(id: string): Promise<void> {
    return http.delete<void>(`/clients/${id}/avatar`);
  },

  bulkUpdate(payload: BulkUpdateRequest): Promise<BulkUpdateResponse> {
    return http.post<BulkUpdateResponse>('/clients/bulk-update', payload);
  },

  forgiveDefaultedSplit(clientId: string, splitId: string): Promise<void> {
    return http.post<void>(
      `/clients/${clientId}/defaulted-splits/${splitId}/forgive`,
      {},
    );
  },

  payDefaultedSplitFromAdvance(clientId: string, splitId: string): Promise<PaySplitDebtResponse> {
    return http.post<PaySplitDebtResponse>(
      `/split-debts/${splitId}/clients/${clientId}/pay-from-advance`,
      {},
    );
  },

  forgetClient(clientId: string, request: ForgetClientBody): Promise<void> {
    return http.post<void>(`/clients/${clientId}/forget`, request);
  },

  sendLoginCode(clientId: string): Promise<void> {
    return http.post<void>(`/clients/${clientId}/login-code`, {});
  },

  linkAccount(clientId: string): Promise<void> {
    return http.post<void>(`/clients/${clientId}/link-account`, {});
  },

  adminCorrectProfile(
    id: string,
    payload: AdminCorrectProfilePayload,
  ): Promise<void> {
    return http.patch<void>(`/admin/clients/${id}/profile`, payload);
  },

  assignCategory(id: string, payload: AssignClientCategoryPayload): Promise<void> {
    return http.put<void>(`/clients/${id}/category`, payload);
  },

  setParallelCourts(id: string, payload: SetClientParallelCourtsPayload): Promise<void> {
    return http.put<void>(`/clients/${id}/parallel-courts`, payload);
  },

  getChildren(parentId: string, call?: { signal?: AbortSignal }): Promise<ChildDto[]> {
    return http.get<ChildDto[]>(`/clients/${parentId}/children`, { signal: call?.signal });
  },
  linkChild(parentId: string, payload: LinkChildAtDeskRequest): Promise<ChildDto> {
    return http.post<ChildDto>(`/clients/${parentId}/children`, payload);
  },
  setChildCategories(parentId: string, childId: string, categories: ChildCategories): Promise<void> {
    return http.put<void>(`/clients/${parentId}/children/${childId}/categories`, categories);
  },
  removeChild(parentId: string, childId: string): Promise<void> {
    return http.delete<void>(`/clients/${parentId}/children/${childId}`);
  },

  getClientFriends(clientId: string, call?: { signal?: AbortSignal }): Promise<ClientFriendsResponse> {
    return http.get<ClientFriendsResponse>(`/clients/${clientId}/friends`, { signal: call?.signal });
  },
};
